import { get, once } from "nodash"
import { RegionIds } from "configuration/locations"
import { initialMapViewStates, MapViewState } from "./config"
import { WebMercatorViewport } from "integrations/deckgl"
import { BoundaryBoxCoordinates, WebMercatorViewportType } from "./types"
import { uploadMapSnapshotToS3 } from "plugins/shared/uploadMapSnapshotToS3"
import store from "integrations/redux/store"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "./control/selectors"
import { updateFire } from "features/entity-caching/fires/thunks"
import {
    setProjectBackgroundFinishedUpdating,
    setProjectBackgroundUpdating,
} from "features/entity-caching/fires/slice"
import { getMapDimensions } from "./control/useMapControl"
import { getViewport, getViewState } from "./GlobalViewState"
import { waitForIdleMap } from "features/map/waitForIdleMap"

// eventually we should pick this up from a combination of local state, profile and organization default values.
// but for now this quick and dirty will have to do...
export const getMapViewStateFromRegion = (regionId?: string) => {
    const result = get(
        initialMapViewStates,
        regionId ?? "",
        initialMapViewStates[RegionIds.BritishColumbia]
    )

    return result
}

const noItemsWarning = () =>
    console.warn("Skipping calculateIdealViewportFromData because items attribute is empty")

const noUpdateResponse = { requiresUpdate: false }
export const calculatePreferredViewStateFromBoundaryBox = (
    currentViewState: MapViewState,
    boundaryBox: BoundaryBoxCoordinates,
    // asssuming this to be px
    defaultViewportPadding = 300,
    zoomLevel?: number,
    preserveZoom?: boolean
) => {
    if (Object.keys(boundaryBox).length === 0) {
        once(noItemsWarning)

        return noUpdateResponse
    }

    try {
        const { minLatitude, minLongitude, maxLatitude, maxLongitude } = boundaryBox
        // this is the recommended way to generate a non-real representation that can calculate the view bounds for us
        const mapDimensions = getMapDimensions()
        const clampedViewState = {
            ...currentViewState,
            ...mapDimensions,
        }
        const safePadding = Math.min(
            defaultViewportPadding,
            Math.floor(mapDimensions.width / 2) - 1,
            Math.floor(mapDimensions.height / 2) - 1
        )

        const viewport = new WebMercatorViewport(clampedViewState)

        // for reference: https://github.com/visgl/deck.gl/blob/master/modules/core/src/viewports/web-mercator-viewport.ts
        const { longitude, latitude, zoom } = viewport.fitBounds(
            [
                [minLongitude, minLatitude],
                [maxLongitude, maxLatitude],
            ],
            {
                padding: safePadding,
                maxZoom: 18,
            }
        ) as WebMercatorViewportType

        const preferredZoom = preserveZoom ? getViewState().zoom : zoomLevel || zoom

        const requiresUpdate =
            currentViewState.latitude !== latitude ||
            currentViewState.longitude !== longitude ||
            currentViewState.zoom !== preferredZoom

        return { longitude, latitude, zoom: preferredZoom, requiresUpdate }
    } catch (error) {
        console.warn("WebMercatorViewport failed to initialize for an unknown reason", {
            error,
            currentViewState,
            boundaryBox,
        })
    }

    return noUpdateResponse
}

const SCALE = 0.3
const lastSnapshotAt = new Map<string, number>()
export const getLastSnapshotTimestamp = (projectId: string) => lastSnapshotAt.get(projectId)

const takeMapSnapshotUnthrottled = async () => {
    // let maps put in the tile requests, setting timeout to 0 worked for maplibre but didn't for deckgl, so I added 100ms delay
    await new Promise((res) => setTimeout(res, 100))
    const success = await waitForIdleMap()
    if (!success) return console.warn("Skipping map snapshot")
    const state = store.getState()
    const orgId = selectActiveWorkspaceId()(state)
    const projectId = selectActivatedFireId(state)
    if (!orgId || !projectId) return

    store.dispatch(setProjectBackgroundUpdating(projectId))

    const mapContainer = document.getElementById("map")
    if (!mapContainer) return
    const canvas = mapContainer.querySelector("canvas.maplibregl-canvas") as HTMLCanvasElement
    if (!canvas) return
    const { width, height } = canvas
    lastSnapshotAt.set(projectId, Date.now())

    const offscreenCanvas = document.createElement("canvas")
    offscreenCanvas.width = width * SCALE
    offscreenCanvas.height = height * SCALE
    const offscreenCtx = offscreenCanvas.getContext("2d")
    if (!offscreenCtx) {
        console.error("Could not create 2D context on offscreen canvas")
        return
    }
    offscreenCtx.drawImage(canvas, 0, 0, width, height, 0, 0, width * SCALE, height * SCALE)
    const blob = await new Promise<Blob>((res, rej) =>
        offscreenCanvas.toBlob((blob) => {
            if (!blob) return rej()
            res(blob)
        })
    )

    try {
        const { longitude, latitude, zoom } = getViewport()
        const thumbnailUrl = await uploadMapSnapshotToS3(blob, orgId, projectId)

        const updates: Record<string, unknown> = { thumbnailUrl }
        if (longitude !== undefined && latitude !== undefined && zoom !== undefined)
            updates.thumbnailViewState = { longitude, latitude, zoom }

        await store.dispatch(
            updateFire({
                id: projectId,
                updates,
            })
        )
    } catch {
        console.warn("thumbnail upload failed, continuing with previous thumbnail")
    } finally {
        store.dispatch(setProjectBackgroundFinishedUpdating(projectId))
    }
}

function keyScopedThrottle<T extends (...args: unknown[]) => void>(fn: T, wait: number) {
    const map = new Map<string, number>()

    return (key: string, ...args: unknown[]) => {
        const now = Date.now()
        const lastCall = map.get(key) ?? 0
        if (now - lastCall >= wait) {
            map.set(key, now)
            fn(...(args as Parameters<T>))
        }
    }
}

export const takeMapSnapshot = keyScopedThrottle(takeMapSnapshotUnthrottled, 120_000)
